<script setup lang="ts">
import { computed } from 'vue';
import {
  CASHBACK_DEFAULT_THEME,
  CASHBACK_FALLBACK_URL,
  type CashbackItemData,
} from '@/utils/cashback-items';

// A lista, as cores e o tema dourado moram em utils/cashback-items.ts — este
// componente só desenha o item que recebe.
const props = defineProps<{ item: CashbackItemData }>();

const emit = defineEmits<{
  (e: 'select', url: string): void;
}>();

const isEnabled = computed(() => props.item.enabled !== false);

// O item sobrescreve só o que precisa da paleta padrão.
const theme = computed(() => ({
  ...CASHBACK_DEFAULT_THEME,
  ...props.item.theme,
}));

const cardStyle = computed(() => ({
  background: theme.value.card,
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.03)',
}));

// Selo ao lado do nome — o chip "VIP" do header no Figma (node `Vips-icon`):
// gradiente diagonal quente com o texto em PRETO, não branco. Os valores saem de
// lá: 15px de altura, texto 8.41/8.88px extrabold. O raio de 7.691px do design é
// ≥ metade da altura, ou seja já é uma cápsula — daí `rounded-full`.
const BADGE_GRADIENT =
  'linear-gradient(116.67deg, #DA812C 2.917%, #FFCC97 34.26%, #FFA45B 55.021%, #994219 97.51%)';

const pillStyle = computed(() => ({
  background: theme.value.pill,
  color: theme.value.pillText,
  fontSize: '19.671px',
  fontStyle: 'italic',
  fontWeight: 900,
  lineHeight: '17.751px',
}));

const handleClick = () => {
  emit('select', props.item.url || CASHBACK_FALLBACK_URL);
};

// Duas classes aqui não são cosméticas:
// - `transition`: o reset global do main.scss zera `transition` em tudo, exceto
//   em quem carrega essa classe literal.
// - `will-change-transform`: mantém o card em camada composta própria de forma
//   permanente. Sem isso, o texto é re-rasterizado a cada entrada/saída do
//   hover; quando o card cai em coordenada fracionária (é o caso dentro do modal
//   do Clube Fidelidade, cujo painel é centrado com `margin: auto`), a nova fase
//   de subpixel desloca visivelmente os glifos.
const baseClass =
  'flex items-center justify-between rounded-[12px] p-1 relative h-[52px] transform transition duration-200 ease-out will-change-transform';

// Hover discreto: 1px de elevação + leve brilho. NÃO usar `scale` aqui: ela
// muda a resolução de raster do texto, e ao fim da animação o navegador
// descarta a camada composta e redesenha o texto na escala 1.0 — é o "flick"
// visível no fim do hover (aparecia no modal do Clube Fidelidade). `translate`
// não tem esse efeito porque não reescala nada.
const rootClass = computed(() => {
  if (isEnabled.value) {
    return 'cursor-pointer hover:-translate-y-px hover:brightness-105 active:translate-y-0 focus:outline-none';
  }

  return 'cursor-pointer hover:-translate-y-px active:translate-y-0 focus:outline-none grayscale opacity-60';
});
</script>

<template>
  <div class="w-full">
    <div
      :aria-disabled="!isEnabled"
      :class="[baseClass, rootClass]"
      :style="cardStyle"
      @click="handleClick"
    >
      <div class="flex items-center gap-2">
        <div
          class="w-14 h-14 rounded-full bg-transparent flex items-center justify-center overflow-hidden"
        >
          <NuxtImg
            alt=""
            class="w-full h-full object-contain"
            :draggable="false"
            :src="getAssetsUrlForCDN(props.item.img)"
          />
        </div>
        <div class="flex flex-col">
          <span
            class="text-[10px] font-semibold leading-[12px]"
            :style="{ color: theme.accent }"
            >Cashback</span
          >
          <div class="flex items-center gap-1.5">
            <span
              class="text-[15px] font-bold"
              :style="{ color: theme.cardText }"
              >{{ props.item.name }}</span
            >
            <span
              v-if="props.item.badge"
              class="flex h-[15px] shrink-0 items-center justify-center rounded-full px-2 text-[8.41px] font-extrabold leading-[8.88px] text-black"
              :style="{ background: BADGE_GRADIENT }"
              >{{ props.item.badge }}</span
            >
          </div>
        </div>
      </div>

      <div
        class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center"
      >
        <div
          class="flex-none inline-flex w-[92px] h-[52px] items-center justify-between px-2 py-2 rounded-[10px]"
          :style="pillStyle"
        >
          <div class="flex flex-col items-center">
            <span class="text-[10px] leading-[11px] font-bold">Até</span>
            <span>{{ props.item.percent }}</span>
          </div>
          <NuxtIcon
            alt="arrow"
            class="w-5 h-5 shrink-0"
            :draggable="false"
            name="menu-lateral/arrow"
          />
        </div>
      </div>
    </div>
  </div>
</template>
